/**
 * Project Setup Wizard
 * Step 0: Tracking Mode
 * Step 1: Project/Yard Details (+ KMZ/KML upload)
 * Step 2: Map Kmz to Areas
 *   - Beacon/Both: KMZ tag association against admin-configured area types
 *   - Manual (AST-87): Structures / Project/Laydown Yards / Other, saved to
 *     the project's area elements after the project is created
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  IconButton,
  Select,
  FormControl,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  AddCircleOutline as AddIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import AppBar from '../elements/AppBar';
import MapKmzToAreas, { emptySelections } from '../elements/MapKmzToAreas';
import SpriteIcon from '../elements/SpriteIcon';
import TrackingModePicker from '../elements/TrackingModePicker';
import ProjectDetailsForm, { isLocationSatisfied as computeIsLocationSatisfied, resolveLocationForSave, GeocodeNotFoundError, GeocodeAuthError, parseLocationLat, parseLocationLng, isValidLat, isValidLng } from '../elements/ProjectDetailsForm';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchProjectsByCustomer } from '../../store/slices/projectSlice';
import { parseKmzFile, coordinatesToGeoJsonString } from '../../utils/kmzParser';
import { indexTree, selectionsToElements } from '../../utils/areaElements';
import { saveAreaElements, areaErrorMessage, storeKmzFile, withSourceFile } from '../../services/areaElementsApi';
import * as api from '../../services/api';
import { colorPalette } from '../../theme';
import { CAPABILITY_MAP } from '../../utils/statusTone';

import areasConfig from '../../../data/areas-config.json';

// AST-87 reverses ASS-74 for Manual: every mode now has three steps. Before a
// mode is chosen the stepper still shows two, so "Map Kmz to Areas" doesn't
// appear ahead of the choice (ASS-80 follow-up).
const ALL_STEPS = ['Tracking Mode', 'Project/Yard Details', 'Map Kmz to Areas'];
const PRE_MODE_STEPS = ['Tracking Mode', 'Project/Yard Details'];

let nextAssocId = 1;

const ProjectSetupWizard = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { activeCustomer, customerList } = useAppSelector((state) => state.customer);
  const { userGroups } = useAppSelector((state) => state.auth);
  const isAdmin = userGroups.includes('admins');

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(activeCustomer?.id || '');

  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    type: 'project',
    status: 'active',
    client: '',
    startDate: '',
    endDate: '',
    trackingMode: '',
    // R-21: manual location as an alternative to KMZ boundary upload.
    // Not required (R-22 covers the incomplete case). All three are strings
    // at the form layer; coordinates are cast to numbers only when a payload
    // is built — keeps the TextField behaviour clean for typing.
    locationAddress: '',
    locationLat: '',
    locationLng: '',
  });

  const [kmzData, setKmzData] = useState(null);
  const [selectedBoundary, setSelectedBoundary] = useState(null);

  // KMZ associations: { [areaTypeId]: [{ id, kmzFeatureId, customName }] }
  const [kmzAssociations, setKmzAssociations] = useState({});

  // AST-87: manual projects' Map Kmz to Areas selections + parsed-tree index.
  const [areaSelections, setAreaSelections] = useState(emptySelections());
  const [kmzIndex, setKmzIndex] = useState(null);
  // AST-98: the chosen File itself, so it can be stored once the project exists.
  const [kmzFile, setKmzFile] = useState(null);

  // ASS-74: manual projects get 2 steps; beacon/hybrid get 3. ASS-80
  // follow-up: before a mode is chosen, show the 2-step list rather than
  // defaulting to 3 -- the stepper previously showed "Map Kmz to Areas"
  // even before Beacon/Both was picked, which Mark has flagged twice on
  // the KMZ control itself; the stepper label is the same defect one
  // level up.
  const isManualProject = projectData.trackingMode === 'manual';
  const isBeaconOrHybrid = projectData.trackingMode === 'beacon' || projectData.trackingMode === 'hybrid';
  const steps = (isBeaconOrHybrid || isManualProject) ? ALL_STEPS : PRE_MODE_STEPS;

  // ASS-74 item 7 / ASS-80: location is required on the Manual details
  // step -- either an address, or both coordinates (valid ranges), or
  // both. Delegated to ProjectDetailsForm's shared isLocationSatisfied so
  // this rule and the Edit modal's rule can never drift apart -- computed
  // here (not just inside handleSubmit) so the Save button's disabled
  // state can gate on it directly. Beacon/Both projects are unaffected --
  // their KMZ boundary satisfies R-22 on its own; this gate only applies
  // when isManualProject is true (see the Save button below).
  const isLocationSatisfied = computeIsLocationSatisfied(projectData);
  // AST-95: same parser as ProjectDetailsForm (handles a pasted typographic
  // minus or dash), so the two can't disagree about what's valid.
  const locationLatNum = parseLocationLat(projectData.locationLat);
  const locationLngNum = parseLocationLng(projectData.locationLng);
  const hasValidLat = isValidLat(locationLatNum);
  const hasValidLng = isValidLng(locationLngNum);
  const hasManualCoords = hasValidLat && hasValidLng;
  const hasManualAddress = projectData.locationAddress.trim().length > 0;

  const isStepValid = (step) => {
    // R-18 step 0: tracking mode must be chosen. No default (R-08 contract).
    if (step === 0) return projectData.trackingMode === 'beacon'
                          || projectData.trackingMode === 'manual'
                          || projectData.trackingMode === 'hybrid';
    // Step 1 (was step 0): project name required. Manual also needs a
    // location before Next (it used to gate Save here), and a file that was
    // chosen must have parsed to at least one placemark with coordinates.
    if (step === 1) {
      if (projectData.name.trim().length === 0) return false;
      if (isManualProject && !isLocationSatisfied) return false;
      if (isManualProject && kmzData && !(kmzIndex && kmzIndex.placemarks.size > 0)) return false;
      return true;
    }
    // Step 2 (was step 1): optional KMZ associations.
    if (step === 2) return true;
    return false;
  };

  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const parsed = await parseKmzFile(file);
      setKmzData(parsed);
      setKmzFile(file);
      // AST-87: index the folder/placemark tree once so step 3 can populate
      // its pickers without a second upload. A new file clears old picks.
      const index = indexTree(parsed.tree, parsed.features);
      setKmzIndex(index);
      setAreaSelections(emptySelections());
      if (index.placemarks.size === 0) {
        setError('That file has no placemarks with coordinates, so it can\'t be mapped to areas. Choose another file or remove it.');
      }
      const boundaryCandidate = parsed.polygons.find(p =>
        p.name.toLowerCase().includes('boundary') ||
        p.name.toLowerCase().includes('project')
      );
      if (boundaryCandidate) setSelectedBoundary(boundaryCandidate);
    } catch (err) {
      setError(`Failed to parse file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddAssociation = (areaTypeId) => {
    setKmzAssociations(prev => ({
      ...prev,
      [areaTypeId]: [
        ...(prev[areaTypeId] || []),
        { id: nextAssocId++, kmzFeatureId: '', customName: '' },
      ],
    }));
  };

  const handleRemoveAssociation = (areaTypeId, assocId) => {
    setKmzAssociations(prev => ({
      ...prev,
      [areaTypeId]: (prev[areaTypeId] || []).filter(a => a.id !== assocId),
    }));
  };

  const handleAssociationChange = (areaTypeId, assocId, field, value) => {
    setKmzAssociations(prev => ({
      ...prev,
      [areaTypeId]: (prev[areaTypeId] || []).map(a =>
        a.id === assocId ? { ...a, [field]: value } : a
      ),
    }));
  };

  const availableKmzFeatures = kmzData
    ? kmzData.features.filter(f => f.id !== selectedBoundary?.id)
    : [];

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const boundaryCoords = selectedBoundary?.geometry?.coordinates?.[0];
      const boundaryGeoJson = boundaryCoords
        ? coordinatesToGeoJsonString(boundaryCoords)
        : null;

      const mappedAreas = Object.entries(kmzAssociations)
        .filter(([, assocs]) => assocs.length > 0)
        .map(([areaTypeId, assocs]) => ({
          areaTypeId,
          associations: assocs
            .filter(a => a.kmzFeatureId)
            .map(a => ({
              customName: a.customName,
              feature: kmzData?.features.find(f => f.id === a.kmzFeatureId),
            }))
            .filter(a => a.feature),
        }));

      // R-22: a project saved without a boundary is Draft; with a boundary it
      // is Active. Areas are optional inside a boundary and do not gate the
      // status transition. `projectData.status` default is 'active'; we
      // downgrade to 'draft' when unmapped so an incomplete project is visibly
      // distinct in the projects table.
      //
      // R-21: a project can also be given a location manually (address
      // and/or lat+lng coordinates) as an alternative to KMZ upload. A
      // project with a manual position is NOT unmapped for R-22's purposes
      // -- a marker can be dropped from either source.
      //
      // ASS-82: resolveLocationForSave does the address-only geocoding.
      // Entered coordinates win and skip the network call entirely; a
      // geocode failure blocks Save here (before the API call) with the
      // exact copy the brief specifies; a 401 from the token itself is a
      // distinct, louder error -- surfaced, not silently swapped.
      let resolvedLocation = null;
      if (hasManualCoords || hasManualAddress) {
        try {
          const resolved = await resolveLocationForSave(projectData);
          resolvedLocation = { address: resolved.address, lat: resolved.lat, lng: resolved.lng, source: resolved.source, placeName: resolved.placeName };
          // Feed locationSource back into the form so ProjectDetailsForm's
          // read-only geocoded-coordinates display can render if this
          // Save is retried/re-rendered before navigation completes.
          if (resolved.source === 'geocoded') {
            setProjectData(prev => ({
              ...prev,
              locationLat: String(resolved.lat),
              locationLng: String(resolved.lng),
              locationSource: 'geocoded',
              locationPlaceName: resolved.placeName,
            }));
          }
        } catch (geocodeErr) {
          if (geocodeErr instanceof GeocodeAuthError) {
            console.error('Mapbox token rejected (401):', geocodeErr.message);
            setError('Address lookup is unavailable — add a latitude and longitude.');
          } else if (geocodeErr instanceof GeocodeNotFoundError) {
            setError("We couldn't find that address — add a latitude and longitude.");
          } else {
            setError(geocodeErr.message || 'Could not resolve the location.');
          }
          setLoading(false);
          return;
        }
      }
      const location = resolvedLocation;

      const isUnmapped = !boundaryGeoJson && !location;
      const resolvedStatus = isUnmapped ? 'draft' : (projectData.status || 'active');

      // Strip the flat locationXxx form-layer keys from the payload; the
      // structured `location` object is the wire shape.
      // eslint-disable-next-line no-unused-vars
      const { locationAddress: _a, locationLat: _lat, locationLng: _lng, locationSource: _src, locationPlaceName: _pn, ...rest } = projectData;

      const projectPayload = {
        ...rest,
        status: resolvedStatus,
        customerId: selectedCustomerId || activeCustomer?.id,
        boundaries: boundaryGeoJson,
        kmzFileName: kmzData?.fileName,
        areaMappings: mappedAreas,
        location,
        createdAt: new Date().toISOString(),
      };

      const result = await api.createProject(projectPayload);
      const newId = result.item?.id || result.id;

      // AST-87: save the manual project's area mapping. The project already
      // exists at this point, so a failure here must not look like a failed
      // create -- land on the project and say the areas weren't saved
      // (they can be mapped again from the project page).
      let areaSaveError = null;
      let areaElements = isManualProject && kmzIndex ? selectionsToElements(areaSelections, kmzIndex) : [];

      // AST-98: keep the file itself, so Map Areas can reopen it later without
      // another upload. Stored first so every element can point at it. A
      // failure here doesn't stop the mapping from saving -- the shapes are
      // still in the payload -- but the user is told the file wasn't kept.
      let fileStoreError = null;
      if (isManualProject && kmzFile) {
        try {
          const stored = await storeKmzFile(newId, kmzFile);
          areaElements = withSourceFile(areaElements, stored?.key);
        } catch (fileErr) {
          fileStoreError = areaErrorMessage(fileErr, "The KMZ file wasn't kept. You can upload it again from Map Areas.");
        }
      }

      if (areaElements.length > 0) {
        try {
          await saveAreaElements(newId, areaElements);
        } catch (areaErr) {
          areaSaveError = areaErrorMessage(areaErr, "The project was created, but its areas weren't saved.");
        }
      }
      if (!areaSaveError && fileStoreError) areaSaveError = fileStoreError;

      // R-20: refresh the projects list for the current customer so a return
      // trip to /projects shows the new row. Fire-and-forget — do not delay
      // the confirmation on it.
      const customerId = selectedCustomerId || activeCustomer?.id;
      if (customerId) {
        dispatch(fetchProjectsByCustomer(customerId));
      }

      // ASS-74: every mode navigates to /projects/<id> immediately. The wizard
      // no longer shows a snackbar — ProjectPage reads location.state and raises
      // its own snackbar with an 'Add inventory' action for manual/both projects.
      // ASS-82 follow-up: when the location was geocoded, ProjectPage's snackbar
      // names the resolved place so the user can catch a wrong match immediately
      // rather than only on a later Edit visit.
      navigate(`/projects/${newId}`, {
        state: {
          justCreated: true,
          trackingMode: projectData.trackingMode,
          geocodedPlaceName: resolvedLocation?.source === 'geocoded' ? resolvedLocation.placeName : null,
          areaSaveError,
          areaCount: areaSaveError ? 0 : areaElements.length,
        },
      });
    } catch (err) {
      // R-20 guard: do NOT show a success toast on failure. This branch surfaces
      // an explicit error (Alert above the buttons); no persistence claim made.
      setError(err.message || 'Failed to save. The project was not created.');
      setLoading(false);
    }
  };

  // ---- R-18 Step 0: Customer + Tracking Mode ----
  // The customer's tracking mode determines which values the picker offers.
  // Admins can pick a different customer here than the app's activeCustomer,
  // so we derive the wizard-scoped capability from selectedCustomerId rather
  // than reading useCapability() (which resolves against activeCustomer).
  const selectedCustomer = customerList.find(c => c.id === selectedCustomerId);
  const wizardTrackingMode = selectedCustomer?.trackingMode || 'beacon';
  const wizardCapability = {
    trackingMode: wizardTrackingMode,
    isManualOnly: wizardTrackingMode === 'manual',
    isBeaconOnly: wizardTrackingMode === 'beacon',
    isHybrid: wizardTrackingMode === 'hybrid',
  };

  const renderStep0 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" color="text.secondary">
        Choose which customer this project is for and how it will be tracked. Tracking mode is required.
      </Typography>

      {/* Customer selector: admins pick from list, regular users auto-assigned */}
      {isAdmin ? (
        <TextField
          label="Customer"
          value={selectedCustomerId}
          onChange={(e) => {
            setSelectedCustomerId(e.target.value);
            // Reset tracking mode when customer changes; new customer's capability
            // may not permit the previously chosen mode.
            setProjectData(prev => ({ ...prev, trackingMode: '' }));
          }}
          select
          fullWidth
          required
        >
          {customerList.filter(c => c.status === 'active' || c.status === 'Active').map(c => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField
          label="Customer"
          value={activeCustomer?.name || ''}
          disabled
          fullWidth
        />
      )}

      <TrackingModePicker
        value={projectData.trackingMode}
        onChange={(v) => setProjectData(prev => ({ ...prev, trackingMode: v }))}
        capability={wizardCapability}
      />

      {projectData.trackingMode && (
        <Typography variant="body2" color="text.secondary">
          Selected: <strong>{CAPABILITY_MAP[projectData.trackingMode]?.label || projectData.trackingMode}</strong>
        </Typography>
      )}
    </Box>
  );

  // ---- Step 1 (was step 0): Project Details + KMZ Upload ----
  const renderStep1 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ASS-80: the name/type/status/client/dates/description/location
          fields are now the shared ProjectDetailsForm, so this screen and
          the Edit modal can't drift apart again. KMZ stays here, outside
          the shared component -- Mark overrides N5's optional boundary
          file on the shared form; the wizard's Beacon/Both path still
          offers it. */}
      <ProjectDetailsForm
        value={projectData}
        onChange={setProjectData}
        locationRequired={isManualProject}
      />

      {/* ASS-74 item 1: KMZ/KML upload + boundary selection are Beacon/Both
          only. Manual projects have no boundary shape to upload -- Mark has
          flagged this control appearing on Manual projects twice (#132
          landed with MANUAL_STEPS removing the wizard's step-3 heading, but
          left this control itself unconditional inside renderStep1 -- the
          real defect). Gated on !isManualProject; Beacon/Both path
          (ALL_STEPS, 3 steps) is otherwise untouched. */}
      {/* AST-87 reverses that for Manual: the upload is back as "Area
          Boundaries (KMZ/KML)", feeding step 3. Manual has no boundary
          polygon picker -- it maps placemarks, not a boundary shape. */}
      {(
        <>
          <Divider />

          {isManualProject && (
            <Typography variant="subtitle1" fontWeight={600} component="h2">
              Area Boundaries (KMZ/KML)
            </Typography>
          )}

          {/* KMZ Upload */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              borderStyle: 'dashed',
              borderWidth: 2,
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            component="label"
          >
            <input type="file" accept=".kmz,.kml" hidden onChange={handleFileUpload} />
            <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="subtitle1">
              {kmzData ? kmzData.fileName : 'Upload KMZ or KML File'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click to select a file
            </Typography>
          </Paper>

          {kmzData && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckIcon color="success" fontSize="small" />
                <Typography variant="body2" fontWeight={500}>
                  {kmzData.featureCount} features loaded
                </Typography>
                <Chip label={`${kmzData.polygons.length} Polygons`} size="small" variant="outlined" />
                <Chip label={`${kmzData.lines.length} Lines`} size="small" variant="outlined" />
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  size="small"
                  aria-label="Remove KMZ/KML file"
                  onClick={() => {
                    setKmzData(null);
                    setSelectedBoundary(null);
                    setKmzIndex(null);
                    setKmzFile(null);
                    setAreaSelections(emptySelections());
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              {isManualProject && kmzIndex && (
                <Typography variant="caption" color="text.secondary">
                  {kmzIndex.placemarks.size} placemark{kmzIndex.placemarks.size === 1 ? '' : 's'} with coordinates in {kmzIndex.folders.size} folder{kmzIndex.folders.size === 1 ? '' : 's'}
                </Typography>
              )}

              {/* Boundary selection (Beacon/Both only) */}
              {!isManualProject && kmzData.polygons.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                    Project Boundary:
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={selectedBoundary?.id || ''}
                      displayEmpty
                      onChange={(e) => {
                        const poly = kmzData.polygons.find(p => p.id === e.target.value);
                        setSelectedBoundary(poly || null);
                      }}
                      sx={{ fontSize: '0.85rem' }}
                    >
                      <MenuItem value="" disabled>Select boundary polygon...</MenuItem>
                      {kmzData.polygons.map(p => (
                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  // ---- Step 2: KMZ Tag Association ----
  const renderStep2 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        KMZ TAG ASSOCIATION:
      </Typography>

      {areasConfig.areaTypes.map((areaType) => {
        const assocs = kmzAssociations[areaType.id] || [];
        return (
          <Box key={areaType.id} sx={{ mb: 1 }}>
            {/* Area type header */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 1,
            }}>
              <SpriteIcon iconName={areaType.icon} size={28} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                {areaType.name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleAddAssociation(areaType.id)}
                sx={{ color: 'text.secondary' }}
                title={`Add KMZ mapping for ${areaType.name}`}
              >
                <AddIcon />
              </IconButton>
            </Box>

            {/* Association rows */}
            {assocs.map((assoc) => (
              <Box key={assoc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 5, mb: 1 }}>
                <FormControl size="small" sx={{ minWidth: 280, flex: 1 }}>
                  <Select
                    value={assoc.kmzFeatureId}
                    displayEmpty
                    onChange={(e) => handleAssociationChange(areaType.id, assoc.id, 'kmzFeatureId', e.target.value)}
                    sx={{ fontSize: '0.8rem', height: 36 }}
                  >
                    <MenuItem value="" disabled>Select KMZ tag...</MenuItem>
                    {availableKmzFeatures.map(f => (
                      <MenuItem key={f.id} value={f.id}>
                        {typeof f.name === 'string' ? f.name : f.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  placeholder={areaType.name}
                  value={assoc.customName}
                  onChange={(e) => handleAssociationChange(areaType.id, assoc.id, 'customName', e.target.value)}
                  sx={{ minWidth: 180, flex: 0.6, '& input': { fontSize: '0.8rem', height: 20 } }}
                />

                <IconButton
                  size="small"
                  onClick={() => handleRemoveAssociation(areaType.id, assoc.id)}
                  sx={{ color: 'text.secondary' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar />

      <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md" sx={{ py: '40px' }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: '24px' }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step Content */}
        <Box sx={{ minHeight: 300, mb: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress />
            </Box>
          ) : activeStep === 0
            ? renderStep0()
            : activeStep === 1
              ? renderStep1()
              : isManualProject
                ? (
                  <MapKmzToAreas
                    index={kmzIndex}
                    selections={areaSelections}
                    onChange={setAreaSelections}
                    disabled={loading}
                    fileName={kmzData?.fileName}
                    skipped={kmzIndex?.skipped || 0}
                  />
                )
                : renderStep2()}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Bottom buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="text"
            onClick={() => navigate('/projects')}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>

          {activeStep < steps.length - 1 ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeStep > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(activeStep - 1)}
                  disabled={loading}
                  sx={{ textTransform: 'none' }}
                >
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => setActiveStep(activeStep + 1)}
                disabled={!isStepValid(activeStep) || loading}
                sx={{ backgroundColor: colorPalette.asseTracOrange, textTransform: 'none' }}
              >
                {isManualProject && activeStep === 1 ? 'Next' : 'Continue'}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* AST-87: every mode's last step is Map Kmz to Areas now, so
                  Back (to Project/Yard Details) is shown for Manual too. */}
              {(
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(activeStep - 1)}
                  disabled={loading}
                  sx={{ textTransform: 'none' }}
                >
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !isStepValid(activeStep) || (isManualProject && !isLocationSatisfied)}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                sx={{ backgroundColor: colorPalette.asseTracOrange, textTransform: 'none' }}
              >
                {isManualProject ? 'Save' : 'Create'}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
      </LocalizationProvider>

      {/* ASS-74 item 3: the wizard's own Snackbar was orphaned by the R-20
          navigation change above -- ProjectPage now owns the post-create
          confirmation (reads location.state and raises its own snackbar with
          an 'Add inventory' action). This copy never rendered anything a
          user could see; removed along with its state. */}
    </Box>
  );
};

export default ProjectSetupWizard;
